import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslatorPipe } from '../services/translator.pipe';
import { link, breadcrumb } from '../admin/layout/header/header.component';
import { cloneDeep } from 'lodash';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DataService } from '../services/data.service';
import { OrgUnitsService } from '../services/org-units.service';
import { PermissionHandlerService } from '../services/permission-handler.service';
import { GlobalConstants } from '../services/global-constants';
import { ThemeColor, ThemeService } from '../services/theme.service';

@Component({
  selector: 'app-theme-settings',
  templateUrl: './theme-settings.component.html',
  styleUrl: './theme-settings.component.scss'
})
export class ThemeSettingsComponent implements OnInit , OnDestroy{
  GlobalConstants = GlobalConstants;
  staticURL = GlobalConstants.staticFilesURL;
  loading = true
  selectedOu: any
  color1: any
  header = this.translate('theme settings');
  links: link[] = [];
  breadcrumbs: breadcrumb[] = [{ name: this.translate('theme settings'), link: '/theme-settings' }];
  submittedColors: { variable: string; color: string }[] = [];
  constructor(
    private dataService: DataService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private orgUnits: OrgUnitsService,
    private permissionService: PermissionHandlerService,
    private themeService: ThemeService,
  ) { }

  selectedColors: string[] = [];

  // The theme the signed-in user is actually on (their OU's theme, else the
  // default), as found on :root when the page opened. The pickers preview other
  // themes by writing to :root, so this is what gets put back on leaving.
  activeColors: string[] = [];

  async ngOnInit() {
    // The app refreshes the theme at startup and re-applies it when it lands.
    // Wait for that, or it could land after the colours below are read.
    await this.themeService.ready;
    this.activeColors = this.flatVariables().map(variable => this.getCssVariable(variable));
    this.selectedColors = [...this.activeColors];

    Promise.all([
      this.getTreeData(),
      // With no OU selected the page edits the default theme, so it has to show
      // the saved default. It used to show whatever was on :root, which for a
      // user whose OU has its own theme is that theme, not the default. Saving
      // then sent those colours back as the "default", overwriting the last one.
      this.loadDefaultTheme(),
    ]).then(() => {
      this.loading = false
    })
  }

  ngOnDestroy(): void {
    this.showColors(this.activeColors);
  }

  flatVariables(): string[] {
    return this.themeColors.flatMap(group => group.variables);
  }

  // Put a saved theme into the pickers and preview it on :root. A saved theme
  // can be missing variables added to `themeColors` after it was saved; those
  // fall back to the user's active colours.
  showTheme(values: ThemeColor[]) {
    this.showColors(this.colorsFor(values));
  }

  colorsFor(values: ThemeColor[]): string[] {
    const saved = new Map(values.map(item => [item.variable, item.color]));
    return this.flatVariables().map((variable, i) => saved.get(variable) || this.activeColors[i]);
  }

  showColors(colors: string[]) {
    this.selectedColors = [...colors];
    this.flatVariables().forEach((variable, i) => this.updateCssVariable(variable, colors[i]));
  }

  // Refreshed first: another admin may have saved a default since the app
  // started. `apply: false` because this page previews on :root itself.
  async loadDefaultTheme() {
    await this.themeService.refresh(false);
    const values = this.themeService.stored('themeDefault');
    this.showColors(values.length ? this.colorsFor(values) : this.activeColors);
  }

  getCssVariable(variable: string): string {
    return getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
  }

  // Set CSS variable on :root
  updateCssVariable(variableName: string, value: string) {
    document.documentElement.style.setProperty(variableName, value);
  }

  // The variables a tenant's saved theme controls.
  //
  // This list is the contract: `submitThemeColors` writes exactly these to the
  // DB, and ThemeService sets exactly these back onto :root. A
  // variable that is not here keeps its default from `styles.scss` forever, so
  // anything the look genuinely depends on has to be listed.
  //
  // It now covers the whole palette rather than a slice of it. PrimeNG and the
  // Bootstrap surfaces read these same variables, so a saved theme moves the
  // tables, inputs, dialogs and menus with the rest of the app instead of
  // leaving them on the built-in dark values.
  //
  // Two rules for anything added here:
  //  - it must be declared in `styles.scss` as a literal colour, not as another
  //    `var()`. The colour pickers seed themselves from `getComputedStyle`, and
  //    a `var(--x)` reference comes back as that raw string, not a colour.
  //  - it must appear once. `getColorIndex` maps this nested list onto one flat
  //    `selectedColors` array by position, so a variable listed twice gets two
  //    pickers that disagree and two conflicting rows in the saved payload.
  //    --color-light-blue and --color-table-th used to be listed twice.
  // Headings are unchanged: they go through the translator pipe and every one of
  // them has an entry in `misc/data/arabics.json`. A new heading would render as
  // raw English in the Arabic UI.
  themeColors = [
    {
      heading: 'Theme Content Background Colors',
      variables: [
        '--color-content-bg',        // page / recessed
        '--color-content-bg-white',  // raised card
        '--color-dark-blue',         // primary action + headings
        '--color-light-blue',
        '--color-table-th',          // table header band
        '--color-sticky-border',     // borders, dividers, scrollbar thumb
        '--color-slate',             // control outlines, placeholders
      ]
    },
    {
      heading: 'Text Colors',
      variables: [
        '--color-text-white',        // label on a filled control
        '--color-text-grey',         // secondary / body text
        '--color-text-ink',          // primary text on a surface
        '--color-grey-text',
      ]
    },
    {
      heading: 'Navbar Colors',
      variables: [
        // The navbar, the footer and every dialog header used to be painted with
        // a gradient built from --color-light-blue and --color-dark-blue, so a
        // tenant moved them by moving its buttons. They are a flat band now, and
        // this is the variable behind it — listed here so tenants keep control of
        // it rather than being stuck with the default.
        '--color-app-header',
        '--color-nav-white',
        '--color-nav-grey'
      ]
    },
    {
      heading: 'Button Hover Colors',
      variables: [
        // --color-light-blue and --color-table-th used to be repeated here from
        // the first group. `getColorIndex` flattens this nested list onto one
        // `selectedColors` array by position, so a repeat meant two pickers for
        // one variable that could disagree, and two conflicting rows in the
        // saved payload.
        '--color-btn-hover',
        '--color-dark-blue-hover',
        '--color-dark-blue-active',
      ]
    },
    {
      heading: 'User Profile & Dark Blue Text',
      variables: [
        // These were commented out, which left the heading rendering above an
        // empty row. Both carry real weight now — the chip fill and the text on
        // it — so they are the two the heading already names.
        '--color-user-profile-active',
        '--color-dark-blue-text'
      ]
    },
    {
      heading: 'Home Page Colors',
      variables: [
        '--color-kg-basic',
      ]
    }
  ];

  getColorIndex(groupIndex: number, variableIndex: number): number {
    let index = 0;
    for (let i = 0; i < groupIndex; i++) {
      index += this.themeColors[i].variables.length;
    }
    return index + variableIndex;
  }


  // Throw away unsaved edits: back to the selected OU's saved theme, or to the
  // saved default when no OU is selected.
  resetToDefault() {
    if (this.selectedOu) {
      this.getOu();
    } else {
      this.loadDefaultTheme();
    }
  }

  submitThemeColors( defaultTheme = false) {
    const result: { variable: string; color: string }[] = [];

    let index = 0;
    this.themeColors.forEach(group => {
      group.variables.forEach(variable => {
        result.push({
          variable,
          color: this.selectedColors[index]
        });
        index++;
      });
    });

    this.submittedColors = result;

    // Log it or pass to API
    console.log('Submitted Theme Colors:', this.submittedColors);
    this.saveTheme(this.submittedColors, defaultTheme)
  }


  async getOu() {
    try {
      if (!this.selectedOu) {
        await this.loadDefaultTheme();
        return;
      }
      let res = await this.dataService.getData('ou/theme?id=' + this.selectedOu._id);
      const theme = res?.data?.[0]?.theme;
      const values = theme?.active ? this.themeService.clean(theme.values) : [];
      // An OU without an active theme of its own gets the default at login, so
      // that is what it should start from here.
      if (res.error || !values.length) {
        await this.loadDefaultTheme();
        return
      }
      this.showTheme(values);
    } catch (e) {
      console.log(e);
    }
  } 

  applyThemeColors(themeColors: { variable: string; color: string }[]): void {
    themeColors.forEach(item => {
      document.documentElement.style.setProperty(item.variable, item.color);
    });
  }

  async saveTheme(theme: any, isDefaultTheme: boolean = false) {
    try {
      this.loading = true;

      let endpoint = isDefaultTheme ? 'ou/save-default-theme' : 'ou/';
      let payload = isDefaultTheme ? 
        { theme } : 
        {
          _id: this.selectedOu._id,
          theme: {
            active: true,
            values: theme
          }
        };

      let res: any = await this.dataService.postData(endpoint, payload, isDefaultTheme ? 'POST' : 'PUT');
      
      if (res.error) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate('Error'),
          detail: this.translate('Failed to update theme')
        });
        this.loading = false;
        return;
      }

      this.messageService.add({
        severity: 'success',
        summary: this.translate('Success'),
        detail: this.translate('Theme updated successfully')
      });

      this.loading = false;
      // The save may have changed the user's own theme (their OU's, or the
      // default they are on). Take it from the server rather than guessing, so
      // leaving the page keeps what they should now see.
      await this.themeService.refresh(false);
      this.activeColors = this.colorsFor(this.themeService.stored('theme'));

    } catch (error) {
      console.error('Error saving theme:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Error'),
        detail: this.translate('An error occurred while saving the theme')
      });
      this.loading = false;
    }
  }

 

  translate(key: string) {
    let translator: TranslatorPipe = new TranslatorPipe();
    return translator.transform(key);
  }

  checkisImg(arg0: string | undefined): any {
    if (arg0) {
      return /\.(jpg|jpeg|png|gif|svg)/.test(arg0);
    } else {
      return false;
    }
  }

  treeData: any[] = [];
  selectedTreeData: any = [];
  selectedTreeTags_current: any;
  selectedTreeData_orginal: any;

  async getTreeData() {
    this.treeData = await this.permissionService.extractOUChildren('CONTENT_UPDATE_LOGS', 'r');
    this.selectedTreeData_orginal = cloneDeep(this.treeData)
    this.changeSelectedTag(null);
    console.log("===>test data d", this.selectedTreeData);
  }

  findNodeByName(data: any[], nodeName: any): any | undefined {
    for (const node of data) {
      if (node.children) {
        const result = this.findNodeByName(node.children, nodeName);
        if (result) {
          return result;
        }
      }
    }
    return undefined;
  }

  async changeSelectedTag(event: any) {
    console.log("change tag event", event);
    this.selectedTreeTags_current = {
      "label": "Ministries/Entities"
    };
    if (this.selectedTreeTags_current.label) {
      console.log("change tag event1", event, this.selectedTreeData);
      await this.filterTreeByTag("Ministries/Entities");
    } else {
      this.selectedTreeData = null;
    }
  }

  filterTreeByTag(tag: string) {
    this.selectedTreeData = cloneDeep(this.selectedTreeData_orginal);
    this.selectedTreeData = this.selectedTreeData
      .map((node: any) => this.filterNodes(node, tag))
      .filter((filteredNode: any) => filteredNode !== null);
    console.log(this.selectedTreeData);

    if (this.selectedTreeData.length == 0) {
      let cloneitems: any = []
      this.selectedTreeData_orginal.forEach((item: any) => {
        cloneitems.push({
          data: item.data,
          id: item.id,
          label: item.label,
          parent: item.parent,
          tag: item.tag,
          _id: item._id,
          children: []
        })
      });

      console.log("cloneitems.children", cloneitems);
      this.selectedTreeData = cloneDeep(cloneitems);
      console.log("cloneitems.children this.selectedTreeData", this.selectedTreeData);
    }
  }

  filterNodes(node: any, tag: string): any | null {
    if (node.children && node.children.length > 0) {
      node.children = node.children
        .map((childNode: any) => this.filterNodes(childNode, tag))
        .filter((filteredChild: any) => filteredChild !== null);
    }

    if (node.tag === tag || (node.children && node.children.length > 0)) {
      return node;
    }
    return null;
  }

}
